import { Icon as IconifyIcon } from '@iconify/react';
import { styled } from '@linaria/react';
import { Group, Textarea } from '@mantine/core';
import type { KeyboardEvent } from 'react';
import { useCallback, useRef } from 'react';
import type { SlashCommand } from '../../../../api/agents-client/agent-client.type';
import { Tooltip } from '../../../../ui-library/tooltip/tooltip';
import { Text } from '../../../../ui-library/typography/typography';
import { ContextUsageIndicator } from '../context-usage-indicator/context-usage-indicator';
import { FloraApprovalModeToggle } from '../flora-approval-mode-toggle/flora-approval-mode-toggle';
import {
  InputBox,
  SendButton,
  ToolbarLeft,
  ToolbarRight,
  ToolbarRow,
  TriggerBtn,
} from '../flora-input-composer.styles';
import { formatMentionForMessage } from '../flora-mention/flora-mention.helpers';
import type { MentionItem, SelectedMention } from '../flora-mention/flora-mention.type';
import { MentionPill } from '../flora-mention/mention-pill';
import { MentionPopover } from '../flora-mention/mention-popover';
import { useMention } from '../flora-mention/use-mention';
import type { TokenUsage } from '../../flora.type';
import { AgentAttachmentChip } from './agent-attachment-chip';
import { AgentModelSelector } from './agent-model-selector';
import { FloraTodoBar } from '../flora-todo-bar/flora-todo-bar';
import type { AgentTodo } from '../flora-todo-bar/flora-todo-bar.type';
import { SlashCommandPopover } from './slash-commands/slash-command-popover';
import { useComposerFileDrop } from './hooks/use-composer-file-drop';
import { useSlashCommandInput } from './slash-commands/use-slash-command-input';
import { FLORA_INPUT_PLACEHOLDER } from './chat.constants';
import type { AgentAttachmentsBinding } from './use-agent-attachments';
import { ACCEPTED_ATTACHMENT_EXTENSIONS } from './use-agent-attachments.helpers';

interface AgentInputProps {
  question: string;
  onQuestionChange: (value: string) => void;
  onSubmit: (formattedMessage?: string, displayMessage?: string) => void;
  isActiveQuestion: boolean;
  tokenUsage?: TokenUsage | null;
  dataTheme?: string;
  attachments: AgentAttachmentsBinding;
  onStop?: () => void;
  /** The open chat's thread id, so the approval-mode toggle switches only this chat. */
  threadId?: string;
  /** Current turn's todo plan, rendered as a collapsible bar atop the composer. */
  todos?: AgentTodo[] | null;
  /** Whether the todo-owning turn is still streaming (drives live indicators). */
  todosLive?: boolean;
  /** Accuracy disclaimer under the composer; narrow hosts pass a shorter one to avoid wrapping. */
  disclaimer?: string;
}

const DEFAULT_DISCLAIMER =
  'Flora can make mistakes. Responses may not be entirely accurate and forecasts are estimates.';

export function AgentInput({
  question,
  onQuestionChange,
  onSubmit,
  isActiveQuestion,
  tokenUsage,
  dataTheme,
  attachments,
  onStop,
  threadId,
  todos,
  todosLive,
  disclaimer = DEFAULT_DISCLAIMER,
}: AgentInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const inputWrapperRef = useRef<HTMLDivElement>(null);

  const {
    isOpen: isMentionOpen,
    searchQuery: mentionSearchQuery,
    allItems,
    boards,
    selectedMentions,
    handleInputChange: handleMentionInputChange,
    handleSelectMention,
    handleRemoveMention,
    closeMentionPopover,
    clearMentions,
  } = useMention(question);

  const {
    isOpen: isSlashOpen,
    searchQuery: slashSearchQuery,
    filteredGroups,
    flatFilteredCommands,
    selectedCommand,
    handleInputChange: handleSlashInputChange,
    handleSelectCommand,
    closePopover: closeSlashPopover,
    clearCommand,
    formatMessageWithCommand,
  } = useSlashCommandInput(question);

  const canSubmit = question.trim().length > 0 && !isActiveQuestion && !attachments.hasUploading;

  const { isDraggingOver, dropZoneRef, dropHandlers, fileInputRef, fileInputProps, triggerFilePicker } =
    useComposerFileDrop({ onFiles: attachments.addFiles, accept: ACCEPTED_ATTACHMENT_EXTENSIONS });

  const formatMessageWithMentions = useCallback((text: string, mentions: SelectedMention[]): string => {
    if (mentions.length === 0) return text;
    let formattedText = text;
    const sortedMentions = [...mentions].sort((a, b) => {
      const aPos = formattedText.indexOf(`[@${a.name}]`);
      const bPos = formattedText.indexOf(`[@${b.name}]`);
      return bPos - aPos;
    });
    for (const mention of sortedMentions) {
      const placeholder = `[@${mention.name}]`;
      formattedText = formattedText.replace(placeholder, formatMentionForMessage(mention));
    }
    return formattedText;
  }, []);

  const handleSubmit = useCallback(() => {
    if (!question.trim()) return;

    // Format mentions first
    const withMentions = formatMessageWithMentions(question, selectedMentions);

    // If a slash command is selected, format for backend (replace /name with prompt)
    // and keep the display version with /commandname visible
    if (selectedCommand) {
      const backendMessage = formatMessageWithCommand(withMentions, selectedCommand);
      const displayMessage = withMentions;
      clearCommand();
      clearMentions();
      onSubmit(backendMessage, displayMessage);
    } else {
      clearMentions();
      onSubmit(withMentions);
    }
  }, [
    question,
    selectedMentions,
    selectedCommand,
    formatMessageWithMentions,
    formatMessageWithCommand,
    clearCommand,
    clearMentions,
    onSubmit,
  ]);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Slash popover keyboard delegation (checked first)
    if (isSlashOpen && (e.key === 'Enter' || e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Tab')) {
      return; // Let the popover's document-level listener handle it
    }
    if (e.key === 'Escape' && isSlashOpen) {
      e.preventDefault();
      closeSlashPopover();
      return;
    }

    // Mention popover keyboard delegation
    if (isMentionOpen && (e.key === 'Enter' || e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Tab')) {
      return;
    }
    if (e.key === 'Escape' && isMentionOpen) {
      e.preventDefault();
      closeMentionPopover();
      return;
    }

    // Normal submit
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (canSubmit) handleSubmit();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.currentTarget.value;
    const cursorPosition = e.currentTarget.selectionStart || 0;
    onQuestionChange(value);

    handleSlashInputChange(value, cursorPosition);
    handleMentionInputChange(value, cursorPosition);
  };

  const handleMentionSelect = (item: MentionItem) => {
    const { newValue, newCursorPosition } = handleSelectMention(item);
    onQuestionChange(newValue);
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
      }
    }, 0);
  };

  const handleSlashCommandSelect = (command: SlashCommand) => {
    const { newValue, newCursorPosition } = handleSelectCommand(command);
    onQuestionChange(newValue);
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
      }
    }, 0);
  };

  const handlePillRemove = (mentionId: string, mentionName: string) => {
    const placeholder = `[@${mentionName}]`;
    const newValue = question.replace(placeholder, '').replace(/ {2,}/g, ' ').trim();
    onQuestionChange(newValue);
    handleRemoveMention(mentionId);
  };

  const triggerMention = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    // Close the slash popover first — the two popovers are mutually exclusive in
    // render (`isMentionOpen && !isSlashOpen`), so leaving slash open would hide
    // both once the mention state flips on.
    closeSlashPopover();
    const cursorPos = textarea.selectionStart || question.length;
    const before = question.substring(0, cursorPos);
    const after = question.substring(cursorPos);
    const needsSpace = before.length > 0 && before[before.length - 1] !== ' ' && before[before.length - 1] !== '\n';
    const newValue = before + (needsSpace ? ' @' : '@') + after;
    const newCursorPos = cursorPos + (needsSpace ? 2 : 1);
    onQuestionChange(newValue);
    handleMentionInputChange(newValue, newCursorPos);
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const triggerSlashCommand = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    // Close the mention popover first — see the note in `triggerMention`; the
    // reverse pairing would otherwise leave both popovers hidden.
    closeMentionPopover();
    const cursorPos = textarea.selectionStart || question.length;
    const before = question.substring(0, cursorPos);
    const after = question.substring(cursorPos);
    const needsSpace = before.length > 0 && before[before.length - 1] !== ' ' && before[before.length - 1] !== '\n';
    const newValue = before + (needsSpace ? ' /' : '/') + after;
    const newCursorPos = cursorPos + (needsSpace ? 2 : 1);
    onQuestionChange(newValue);
    handleSlashInputChange(newValue, newCursorPos);
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  return (
    <InputWrapper>
      <InputInner ref={dropZoneRef} $isDropTarget={isDraggingOver} {...dropHandlers}>
        {todos && todos.length > 0 && (
          <TodoBarTuck>
            <FloraTodoBar todos={todos} isLive={todosLive} />
          </TodoBarTuck>
        )}
        {selectedMentions.length > 0 && (
          <MentionPillsContainer>
            <Group gap={6}>
              {selectedMentions.map((mention) => (
                <MentionPill
                  key={`${mention.type}-${mention.id}`}
                  mention={mention}
                  onRemove={() => handlePillRemove(mention.id, mention.name)}
                />
              ))}
            </Group>
          </MentionPillsContainer>
        )}
        {attachments.items.length > 0 && (
          <AttachmentChipsContainer>
            {attachments.items.map((attachment) => (
              <AgentAttachmentChip
                key={attachment.localId}
                attachment={attachment}
                onRemove={attachments.removeAttachment}
              />
            ))}
          </AttachmentChipsContainer>
        )}
        <InputBox ref={inputWrapperRef}>
          <MentionPopover
            isOpen={isMentionOpen && !isSlashOpen}
            items={allItems}
            boards={boards}
            searchQuery={mentionSearchQuery}
            onSelect={handleMentionSelect}
            onClose={closeMentionPopover}
            anchorRef={inputWrapperRef}
            dataTheme={dataTheme}
          />
          <SlashCommandPopover
            isOpen={isSlashOpen && !isMentionOpen}
            groups={filteredGroups}
            flatCommands={flatFilteredCommands}
            searchQuery={slashSearchQuery}
            onSelect={handleSlashCommandSelect}
            onClose={closeSlashPopover}
            anchorRef={inputWrapperRef}
            dataTheme={dataTheme}
          />
          <Textarea
            ref={textareaRef}
            placeholder={FLORA_INPUT_PLACEHOLDER}
            value={question}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            autosize
            minRows={1}
            maxRows={6}
            variant="unstyled"
            styles={{
              input: {
                fontSize: '15px',
                lineHeight: '1.5',
                padding: '0',
                color: 'var(--ink-darkest)',
                '&::placeholder': { color: 'var(--sky-dark)' },
              },
              wrapper: { flex: 1 },
            }}
            style={{ flex: 1 }}
          />
          <ToolbarRow>
            <ToolbarLeft>
              <Tooltip label="Attach a file" position="top" withArrow variant="black">
                <TriggerBtn onClick={triggerFilePicker} type="button" aria-label="Attach file">
                  <IconifyIcon icon="material-symbols:add" width={18} height={18} />
                </TriggerBtn>
              </Tooltip>
              <HiddenFileInput ref={fileInputRef} {...fileInputProps} />
              <Tooltip label="Mention (@)" position="top" withArrow variant="black">
                <TriggerBtn onClick={triggerMention} type="button" aria-label="Mention">
                  <IconifyIcon icon="material-symbols:alternate-email" width={16} height={16} />
                </TriggerBtn>
              </Tooltip>
              <Tooltip label="Commands (/)" position="top" withArrow variant="black">
                <TriggerBtn onClick={triggerSlashCommand} type="button" aria-label="Slash command">
                  <IconifyIcon icon="material-symbols:terminal" width={16} height={16} />
                </TriggerBtn>
              </Tooltip>
              <FloraApprovalModeToggle threadId={threadId} />
              <AgentModelSelector />
            </ToolbarLeft>
            <ToolbarRight>
              <ContextUsageIndicator tokenUsage={tokenUsage ?? null} dataTheme={dataTheme} />
              {isActiveQuestion && onStop ? (
                <SendButton onClick={onStop} aria-label="Stop response" type="button">
                  <IconifyIcon icon="material-symbols:stop-rounded" width={18} height={18} />
                </SendButton>
              ) : (
                <SendButton onClick={handleSubmit} disabled={!canSubmit} aria-label="Send message" type="button">
                  <IconifyIcon icon="material-symbols:arrow-upward-rounded" width={18} height={18} />
                </SendButton>
              )}
            </ToolbarRight>
          </ToolbarRow>
        </InputBox>
      </InputInner>
      <Text size="tiny" color="var(--ink-lighter)" style={{ textAlign: 'center', lineHeight: '1.4' }}>
        {disclaimer}
      </Text>
    </InputWrapper>
  );
}

const InputWrapper = styled.div`
  /* The side panel narrows the gutter via --flora-chat-gutter. */
  padding: 12px var(--flora-chat-gutter, 24px) 10px;
  background: var(--base-white);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const InputInner = styled.div<{ $isDropTarget: boolean }>`
  max-width: var(--content-max-width, 960px);
  width: 100%;
  transition: max-width 0.25s ease;
  display: flex;
  flex-direction: column;
  gap: 8px;
  outline: ${({ $isDropTarget }) => ($isDropTarget ? '2px dashed var(--secondary-base)' : 'none')};
  outline-offset: 2px;
  border-radius: 12px;
`;

/* Cancel InputInner's 8px column gap so the todo bar tucks onto the message
   input's top edge instead of floating above it. */
const TodoBarTuck = styled.div`
  margin-bottom: -8px;
`;

const MentionPillsContainer = styled.div`
  padding: 0 2px;
  display: flex;
  flex-wrap: wrap;
`;

const AttachmentChipsContainer = styled.div`
  padding: 0 2px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const HiddenFileInput = styled.input`
  display: none;
`;
